import {
  all,
  call,
  put,
  select,
  takeLatest,
  takeEvery,
  delay,
} from 'redux-saga/effects';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import ddt from '@wix/da-ddt';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import { withOffset } from '@wix/da-shared-react/pkg/Stream';
import {
  fetchStreamContents,
  takeStreamFetch,
} from '@wix/da-shared-react/pkg/Stream/helpers';
import { STUDIO_VIDEO_GENERATIONS_STREAM } from '../../../constants';
import {
  generateVideo,
  updateVideoCreatorStatus,
  updateVideoCreatorErrorCode,
  updateVideoCreatorRestrictions,
  setFirstGenModalSeen,
  fetchGenerationCost,
  updateVideoCreatorPricePoints,
  updateVideoCreatorModel,
  updateVideoCreatorDuration,
  updateVideoCreatorSound,
  updateVideoCreatorDidCreate,
  updateVideoCreatorIsCalculatingCost,
  updateVideoCreatorPendingGenerationId,
  initializeVideoCreator,
  fetchNextVideoGenerations,
} from '../../actions/studio/videoCreator';
import { receiveHubMessage } from '@wix/da-shared-react/pkg/redux/hub/actions';
import { HubChannel } from '@wix/da-shared-react/pkg/redux/hub/types';
import { setCompliantLocalStorageItem } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/actions';
import { CompliantLocalStorageKeys } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/constants';
import { flagEnabled } from '@wix/da-react-context/pkg/flags/redux/selectors';
import {
  PapiRequestDreamsofartGenerateVideo,
  PapiDreamsofartGenerateVideo,
  PapiRequestDreamsofartVideos,
  PapiDreamsofartVideos,
} from '@wix/da-papi-types';
import {
  getVideoCreatorPrompt,
  getVideoCreatorModel,
  getVideoCreatorAspectRatio,
  getVideoCreatorDuration,
  getVideoCreatorSound,
  getVideoCreatorReferenceDeviation,
  getVideoCreatorPendingGenerationId,
} from '../../selectors/studio/videoCreator';
import { VideoCreatorErrorCode } from '../../types/store';
import { getModelPropertiesByType } from '../../pages/VideoCreator/ModelProperties';

const DEBOUNCE_DELAY = 300;

function* handleGenerateVideo() {
  ddt.log('videoCreator', 'handleGenerateVideo called');
  const prompt: string = yield select(getVideoCreatorPrompt);
  const aspectRatio: string = yield select(getVideoCreatorAspectRatio);
  const duration: number = yield select(getVideoCreatorDuration);
  const sound: boolean = yield select(getVideoCreatorSound);
  const referenceDeviation = yield select(getVideoCreatorReferenceDeviation);
  const selectedModel = yield select(getVideoCreatorModel);

  ddt.log('videoCreator', 'handleGenerateVideo - collected state', {
    prompt,
    aspectRatio,
    duration,
    sound,
    selectedModel,
  });

  const model = getModelPropertiesByType(selectedModel);

  const requestData: PapiRequestDreamsofartGenerateVideo = {
    prompt,
    model_id: model?.id,
    aspect_ratio: aspectRatio,
    duration,
    sound,
  };
  if (referenceDeviation) {
    requestData.reference_deviationid = referenceDeviation?.deviationId;
  }

  yield put(updateVideoCreatorStatus('busy'));

  const response: PapiDreamsofartGenerateVideo = yield call(
    requestPuppy,
    {
      url: 'generate_video',
      method: 'post',
      data: requestData,
    },
    () => ({
      success: true,
      generationId: 5,
      restrictions: {
        pointsBalance: 912,
      },
    }),
    'dreamsofart'
  );

  if (
    !response ||
    isApiError(response) ||
    (response as any).errorCode !== undefined ||
    response.generationId === undefined
  ) {
    ddt.error('videoCreator', 'Failed to generate video', response);
    yield all([
      put(updateVideoCreatorStatus('idle')),
      put(updateVideoCreatorPendingGenerationId(undefined)),
    ]);

    const apiErrorCode = (response as any)?.errorCode;
    let mappedErrorCode: VideoCreatorErrorCode;
    if (
      apiErrorCode !== undefined &&
      typeof apiErrorCode === 'number' &&
      Object.values(VideoCreatorErrorCode).includes(apiErrorCode)
    ) {
      mappedErrorCode = apiErrorCode as VideoCreatorErrorCode;
    } else {
      mappedErrorCode = VideoCreatorErrorCode.OTHER;
    }
    yield put(updateVideoCreatorErrorCode(mappedErrorCode));
  } else {
    yield all([
      put(updateVideoCreatorRestrictions(response.restrictions)),
      put(updateVideoCreatorErrorCode(VideoCreatorErrorCode.NO_ERROR)),
      put(updateVideoCreatorDidCreate(true)),
      put(updateVideoCreatorPendingGenerationId(response.generationId)),
    ]);
    ddt.info('videoCreator', 'Video generation initiated successfully', {
      generationId: response.generationId,
    });
  }
}

function* handleSetFirstGenModalSeen() {
  yield put(
    setCompliantLocalStorageItem({
      key: CompliantLocalStorageKeys.VIDEO_CREATOR_FIRST_GEN_SEEN,
      value: '1',
    })
  );
}

function* handleFetchGenerationCost({
  payload: { modelId, duration, sound },
}: ReturnType<typeof fetchGenerationCost>) {
  yield put(updateVideoCreatorIsCalculatingCost(true));

  const requestData = {
    model_id: modelId,
    duration,
    sound,
  };

  const response: { pricePoints?: number } = yield call(
    requestPuppy,
    {
      url: 'generation_cost',
      method: 'post',
      data: requestData,
    },
    () => ({
      pricePoints: 100,
    }),
    'dreamsofart'
  );

  // Extract pricePoints from response object
  const pricePoints: number | undefined =
    response && typeof response === 'object' && !isApiError(response)
      ? response.pricePoints
      : undefined;

  if (
    !response ||
    isApiError(response) ||
    (response as any)?.errorCode !== undefined ||
    pricePoints === undefined
  ) {
    ddt.error('videoCreator', 'Failed to fetch generation cost', {
      response,
      responseType: typeof response,
      pricePoints,
    });
    yield put(updateVideoCreatorIsCalculatingCost(false));
  } else {
    yield put(updateVideoCreatorPricePoints(pricePoints));
    yield put(updateVideoCreatorIsCalculatingCost(false));
    ddt.info('videoCreator', 'Generation cost fetched successfully', {
      pricePoints,
    });
  }
}

function* handleVideoCreatorPropertyChange() {
  yield delay(DEBOUNCE_DELAY);

  const selectedModel = yield select(getVideoCreatorModel);
  const duration: number = yield select(getVideoCreatorDuration);
  const sound: boolean = yield select(getVideoCreatorSound);

  // Convert model type to model ID
  const model = getModelPropertiesByType(selectedModel);
  if (!model?.id) {
    ddt.warn(
      'videoCreator',
      'Cannot fetch generation cost: model ID not found'
    );
    return;
  }

  // Dispatch the fetch action with current values
  yield put(fetchGenerationCost(model.id, duration, sound));
}

function* handleInitializeVideoCreator(
  action: ReturnType<typeof initializeVideoCreator>
) {
  const { videos } = action.payload.initData;

  if (!videos || !videos.results) {
    ddt.warn('No videos in init data', { videos });
    return;
  }

  ddt.log('Initializing video creator with videos', {
    count: videos.results.length,
    generationIds: videos.results.map(v => v.generationId),
  });

  // Store videos in stream using generationId as the item identifier
  const videoItems = videos.results.map(video => video.generationId);

  // Store video objects in entities for easy access
  const videoEntities = {
    videoGeneration: videos.results.reduce(
      (acc, video) => {
        acc[video.generationId] = video;
        return acc;
      },
      {} as Record<number, (typeof videos.results)[0]>
    ),
  };

  yield put(putEntities({ entities: videoEntities }));
  yield put(
    withOffset.actions.initialize({
      streamId: STUDIO_VIDEO_GENERATIONS_STREAM.streamId,
      itemsPerFetch: 24,
      items: videoItems,
      hasMore: videos.hasMore ?? false,
      nextOffset: videos.nextOffset,
    })
  );

  ddt.log('Video stream initialized', {
    streamId: STUDIO_VIDEO_GENERATIONS_STREAM.streamId,
    itemsCount: videoItems.length,
    hasMore: videos.hasMore ?? false,
  });
}

function* handleHubMessageForVideoCreator(
  action: ReturnType<typeof receiveHubMessage>
) {
  const { channel, data } = action.payload;

  if (channel !== HubChannel.DREAMSOFART) {
    return;
  }

  const dreamupData = data.payload || data.data; // Handle both supabase and centrifuge formats
  if (!dreamupData || !dreamupData.generation_id) {
    return;
  }

  const { generation_id: generationId } = dreamupData;

  const isVMDryRun = yield select(flagEnabled, 'vm_dreamup_dryrun');

  let isVideoGeneration: boolean;
  if (isVMDryRun) {
    // VM dryrun dispatches hub syncronously, so the hub fires before the generate_video call
    // returns. We have to hack it to just skip the isVideoGeneration validation.
    isVideoGeneration = true;
  } else {
    const pendingGenerationId = yield select(
      getVideoCreatorPendingGenerationId
    );
    isVideoGeneration =
      pendingGenerationId !== undefined && pendingGenerationId === generationId;
  }

  if (!isVideoGeneration) {
    return;
  }

  ddt.log('videoCreator', 'Video generation succeeded via hub', {
    generationId,
    fullDreamupData: dreamupData, // Log the full data structure
  });

  // Hub doesn't have all the data we need unfortunately, so just fetch the latest
  // video from the API
  const response: PapiDreamsofartVideos = yield call(
    requestPuppy,
    {
      url: 'videos',
      method: 'get',
      params: {
        offset: 0,
        limit: 1,
      },
    },
    undefined,
    'dreamsofart'
  );

  if (
    isApiError(response) ||
    !response?.results ||
    response.results.length === 0
  ) {
    ddt.error('videoCreator', 'Failed to fetch new video from API', response);
    yield all([
      put(updateVideoCreatorStatus('idle')),
      put(updateVideoCreatorPendingGenerationId(undefined)),
    ]);
    return;
  }

  const videoEntities = {
    videoGeneration: response.results.reduce(
      (acc, video) => {
        acc[video.generationId] = video;
        return acc;
      },
      {} as Record<number, (typeof response.results)[0]>
    ),
  };
  yield put(putEntities({ entities: videoEntities }));

  const videoItems = response.results.map(video => video.generationId);
  yield put(
    withOffset.actions.insertItems({
      streamId: STUDIO_VIDEO_GENERATIONS_STREAM.streamId,
      items: videoItems,
      offset: 0,
    })
  );

  yield all([
    put(updateVideoCreatorStatus('idle')),
    put(updateVideoCreatorPendingGenerationId(undefined)),
  ]);
}

function* handleFetchNextVideoGenerations({
  payload: { streamId },
}: ReturnType<typeof fetchNextVideoGenerations>) {
  const { offset } = yield select(state => ({
    offset: withOffset.selectors.getNextOffset(
      state,
      STUDIO_VIDEO_GENERATIONS_STREAM.streamId
    ),
  }));

  const limit = 10;

  yield fetchStreamContents<
    PapiRequestDreamsofartVideos,
    PapiDreamsofartVideos
  >({
    streamId: STUDIO_VIDEO_GENERATIONS_STREAM.streamId,
    url: 'videos',
    params: {
      offset: offset ?? 0,
      limit,
    },
    papiProject: 'dreamsofart',
    onSuccess: function* (result) {
      // Store video objects in entities
      const videoEntities = {
        videoGeneration: result.results.reduce(
          (acc, video) => {
            acc[video.generationId] = video;
            return acc;
          },
          {} as Record<number, (typeof result.results)[0]>
        ),
      };
      yield put(putEntities({ entities: videoEntities }));
    },
    getStreamData: result => {
      // Store videos in stream using generationId as the item identifier
      const videoItems = result.results.map(video => video.generationId);
      return {
        hasMore: result.hasMore ?? false,
        items: videoItems,
        nextOffset: result.nextOffset,
      };
    },
  });
}

export default function* saga() {
  yield all([
    takeLatest(generateVideo, handleGenerateVideo),
    takeEvery(setFirstGenModalSeen, handleSetFirstGenModalSeen),
    takeEvery(fetchGenerationCost, handleFetchGenerationCost),
    takeEvery(initializeVideoCreator, handleInitializeVideoCreator),
    takeStreamFetch(fetchNextVideoGenerations, handleFetchNextVideoGenerations),
    takeLatest(
      [
        updateVideoCreatorModel,
        updateVideoCreatorDuration,
        updateVideoCreatorSound,
      ],
      handleVideoCreatorPropertyChange
    ),
    // Listen to receiveHubMessage directly to get full hub data
    takeLatest(receiveHubMessage, handleHubMessageForVideoCreator),
  ]);
}
